/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { IRouteParams } from '@/main/routes/constants/routesConstants';
import { icons } from '@assets/index';
import { openCreateInquiry } from '@components/Table/actions/tableActions';
import { ETopicType } from '@components/Table/components/footerModal/CreateInquiry/types/createInquiryTypes';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import { CreateInquiryModal } from '@modules/inquiry/components/CreateInquiryModal';
import { ECreateInquiryType } from '@modules/inquiry/constants';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging';
import { modalOpen } from '@modules/modal/modalActions';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';

import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
} from '../useTableFooterButtons';

const useTableFooterButtonInquiryCreate = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
    id?: string,
) => {
    const dispatch = useDispatch();
    const { clientId } = useParams<IRouteParams>();

    const isInquiriesEnabled = useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.inquiries));

    const openInquiryModal = useCallback(
        (type: ETopicType) => {
            if (isInquiriesEnabled) {
                dispatch(
                    modalOpen({
                        modalParams: {
                            isCustomCrossButton: true,
                            isDisableBackgroundClickClose: true,
                        },
                        render: ({ onCloseModal }) => {
                            return (
                                <CreateInquiryModal
                                    id={id || clientId}
                                    type={
                                        {
                                            [ETopicType.account]: ECreateInquiryType.account,
                                            [ETopicType.subscriberMobile]: ECreateInquiryType.mobile,
                                        }[type]
                                    }
                                    onCloseModal={onCloseModal}
                                />
                            );
                        },
                    }),
                );
            } else {
                const userId = id || clientId || '-1';
                dispatch(openCreateInquiry({ type, userId }));
            }
        },
        [isInquiriesEnabled, dispatch, id, clientId],
    );

    return {
        className: 'table-footer__link link-message',
        dataName: 'message',
        icon: icons.messageIconSmall,
        id: AccessComponentKeyEnum.FooterButtonInquiryCreate,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonInquiryCreate),

        onClick: useCallback(() => {
            logBusinessEvent(ELogBusinessEventName.USER_SHOW_INQUIRY_MASTER, ELogBusinessEventType.common);
            const type = mapTableFooterNameToUrl[tableFooterName];
            if (!type) return;

            openInquiryModal(type);
        }, [openInquiryModal, logBusinessEvent, tableFooterName]),
    };
};

const mapTableFooterNameToUrl: Partial<{ [key in TableFooterNameType]: ETopicType }> = {
    [TableFooterNameType.account]: ETopicType.account,
    [TableFooterNameType.accountId]: ETopicType.account,
    [TableFooterNameType.subscriberId]: ETopicType.subscriberMobile,
    [TableFooterNameType.subscriberMobile]: ETopicType.subscriberMobile,
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonInquiryCreate,
    },
};

export default useTableFooterButtonInquiryCreate;
